/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import { Zap, X, AlertTriangle, EyeOff, Trash2, ShieldCheck, RefreshCw, Mail, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import PurgePreviewPanel from './PurgePreviewPanel';

/**
 * Maker-only controls that float over the dashboard:
 *
 *   • GO LIVE button — hidden by default, summoned via Shift+Ctrl+G.
 *     Only renders if the current session is the Maker AND the platform
 *     is still in sandbox mode. Click triggers a confirm dialog before
 *     hitting POST /api/system/go-live.
 *
 *   • Cloak banner — sticky bottom-left strip when the Maker is cloaked
 *     as another user. One-click "Drop cloak" restores the Maker's own
 *     token (re-login is the cleanest path; banner offers a logout button).
 */
export default function MakerControls() {
  const { user, logout } = useAuth();
  const [systemState, setSystemState] = useState(null);
  const [showGoLive, setShowGoLive] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  const refreshState = useCallback(async () => {
    try {
      const { data } = await api.get('/system/state');
      setSystemState(data);
    } catch {/* ignore */}
  }, []);

  useEffect(() => { refreshState(); }, [refreshState]);

  // Shift+Ctrl+G — show the GO LIVE button (Maker only, sandbox only)
  useEffect(() => {
    const onKey = (e) => {
      if (e.shiftKey && e.ctrlKey && (e.key === 'G' || e.key === 'g')) {
        e.preventDefault();
        if (user?.is_maker && systemState && !systemState.is_live) {
          setShowGoLive((prev) => !prev);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [user, systemState]);

  const triggerGoLive = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post('/system/go-live');
      const purged = Object.values(data.purge_summary || {}).reduce((a, b) => a + b, 0);
      toast.success(`Platform is now LIVE. ${purged} demo records purged.`, { duration: 8000 });
      setSystemState({ is_live: true, mode: 'production' });
      setPurgeResult(data.purge_summary || {});
      setShowGoLive(false);
      console.warn('GO LIVE next step:', data.next_step);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Go-live failed.');
    } finally {
      setConfirming(false);
    }
  };

  const triggerReseed = async () => {
    if (!window.confirm(
      'Reseed sandbox?\n\n'
      + 'This will purge ALL current sandbox demo data (same as GO LIVE) '
      + 'and re-run every seeder so you can rehearse the GO LIVE flow.\n\n'
      + 'AI learning data + audit history + privacy logs are preserved.\n\n'
      + 'Continue?',
    )) return;
    setReseeding(true);
    try {
      const { data } = await api.post('/system/reseed-sandbox');
      const purged = Object.values(data.purge_summary || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `Sandbox reseeded · ${purged} records purged, fresh seeds installed.`,
        { duration: 8000, description: 'Reload to see fresh demo state.' },
      );
      // Reload so the dashboard picks up the fresh seeded counts.
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reseed failed.');
    } finally {
      setReseeding(false);
    }
  };

  // Don't render anything for non-makers / non-cloaked users
  if (!user) return null;
  const isCloaked = user.is_cloaked || user.cloaked_by;
  const inSandbox = systemState && !systemState.is_live;
  const showGoLiveButton = user.is_maker && !isCloaked && inSandbox && showGoLive;

  return (
    <>
      {/* Maker presence dot — bottom-right pulsing gold pip. Click to toggle
          the GO LIVE card. Only visible to the real Maker, only in sandbox. */}
      {user.is_maker && !isCloaked && inSandbox && (
        <button
          onClick={() => setShowGoLive((p) => !p)}
          data-testid="maker-dot"
          title="Maker console (Shift+Ctrl+G)"
          className="fixed bottom-6 right-6 z-[55] h-3 w-3 rounded-full bg-[#8B3FC5] shadow-lg shadow-[#8B3FC5]/40 animate-pulse hover:scale-150 transition-transform"
        />
      )}

      {/* Email-delivery health banner — Maker-only, only when degraded/blocked */}
      {user.is_maker && !isCloaked && <EmailHealthBanner makerEmail={user.email} />}

      {/* GO LIVE floating card (top-right) */}
      {showGoLiveButton && (
        <div
          className="fixed top-20 right-6 z-[60] bg-gradient-to-br from-[#1A0A0A] to-[#0F0606] border border-[#8B3FC5] rounded-xl p-5 shadow-2xl shadow-[#8B3FC5]/20 w-[400px] max-h-[80vh] overflow-y-auto"
          data-testid="maker-go-live-card"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#8B3FC5]/15 flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-[#8B3FC5]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B3FC5]">Maker Console</div>
              <div className="text-base font-semibold text-white mt-1">Go Live — irreversible</div>
              <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                Sandbox routes lock + demo data is purged.
                <strong className="text-emerald-300"> AI memory and audit history are preserved.</strong>
              </p>

              <PurgePreviewPanel active={showGoLiveButton} />

              <div className="mt-3 border border-emerald-500/30 rounded-lg p-3 bg-emerald-500/5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300">Preserved</span>
                </div>
                <p className="text-[11px] text-emerald-100/80 mt-1.5 leading-relaxed">
                  AI curricula · NDS3 strategic context · privacy + sovereignty logs · audit history · all persona AI memories (Iqra · Yusuf · Wujdān · Shu&apos;aib).
                </p>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={triggerGoLive}
                  disabled={confirming || reseeding}
                  data-testid="maker-go-live-confirm"
                  className="flex-1 px-3 py-2 text-xs font-semibold bg-[#8B3FC5] text-[#0A0A0A] rounded-lg hover:bg-[#E5C04E] disabled:opacity-50 transition-colors"
                >
                  {confirming ? 'Flipping…' : 'GO LIVE & PURGE'}
                </button>
                <button
                  onClick={() => setShowGoLive(false)}
                  className="px-3 py-2 text-xs text-slate-400 hover:text-white border border-slate-700 rounded-lg"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>

              {/* Reseed sandbox — rehearses the GO LIVE purge without flipping
                  the irreversible switch. Useful for demos. */}
              <button
                onClick={triggerReseed}
                disabled={confirming || reseeding}
                data-testid="maker-reseed-sandbox"
                className="mt-2 w-full px-3 py-2 text-[11px] font-semibold text-slate-300 hover:text-white border border-slate-700 rounded-lg hover:border-[#8B3FC5] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                title="Purge demo data + re-run all seeders. Lets you rehearse GO LIVE."
              >
                <RefreshCw className={`w-3 h-3 ${reseeding ? 'animate-spin' : ''}`} />
                {reseeding ? 'Reseeding…' : 'Reseed sandbox (rehearsal)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Maker cloak banner — sticky bottom-left when active */}
      {isCloaked && user.is_maker && (
        <div
          className="fixed bottom-6 left-6 z-[55] bg-[#0F0606] border border-[#7C3AED] rounded-xl px-4 py-3 shadow-xl shadow-[#7C3AED]/30 flex items-center gap-3 max-w-[420px]"
          data-testid="maker-cloak-banner"
        >
          <EyeOff className="w-4 h-4 text-[#A78BFA] shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA]">Cloaked Session</div>
            <div className="text-xs text-white mt-0.5">
              Acting as <span className="font-semibold">{user.email}</span> · audit-invisible
            </div>
          </div>
          <button
            onClick={() => { logout(); toast.info('Cloak dropped — sign back in as Maker.'); }}
            data-testid="maker-cloak-drop"
            className="text-[11px] text-[#A78BFA] hover:text-white px-2 py-1 border border-[#7C3AED]/40 rounded-md"
          >
            Drop cloak
          </button>
        </div>
      )}

      {/* Live-mode badge for the Maker — confirms sandbox lockdown took effect */}
      {user.is_maker && systemState?.is_live && (
        <div
          className="fixed top-20 right-6 z-[55] bg-[#0F1A2D] border border-emerald-500/40 rounded-lg px-3 py-2 flex items-center gap-2"
          data-testid="maker-live-badge"
        >
          <AlertTriangle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-300">Production · Sandbox locked</span>
        </div>
      )}

      {/* Post-purge summary panel — appears immediately after GO LIVE completes */}
      {purgeResult && user.is_maker && (
        <div
          className="fixed top-32 right-6 z-[55] bg-[#0F1A0F] border border-emerald-500/40 rounded-lg px-4 py-3 max-w-[340px] shadow-lg"
          data-testid="maker-purge-summary"
        >
          <div className="flex items-center gap-2">
            <Trash2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300">Purged</span>
            <button
              onClick={() => setPurgeResult(null)}
              className="ml-auto text-slate-500 hover:text-white"
              data-testid="maker-purge-summary-close"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="text-[11px] text-slate-200 mt-2 space-y-0.5 max-h-40 overflow-y-auto">
            {Object.entries(purgeResult).map(([k, n]) => (
              <div key={k} className="flex justify-between">
                <span className="truncate">{k}</span>
                <span className="font-mono text-emerald-300 ml-2">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Maker-only email-delivery health banner.
 *
 * Polls `/api/admin/email/health` every 60s. Renders nothing when the
 * status is `ok` or `unknown` (no traffic, no signal). When `degraded`
 * or `blocked` it shows a sticky banner near top-right with:
 *   • verbatim transport error,
 *   • a one-click "Send test to my inbox" button (calls /test-send),
 *   • a direct link to https://hpanel.hostinger.com when SMTP fails.
 *
 * Why not a full-page interstitial? The platform should keep working
 * even with email broken — only the Maker needs to see the warning,
 * and they need it visible without blocking the rest of the UI.
 */
function EmailHealthBanner({ makerEmail }) {
  const [health, setHealth] = useState(null);
  const [testing, setTesting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/email/health');
      setHealth(data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!health || dismissed) return null;
  if (health.status !== 'degraded' && health.status !== 'blocked') return null;

  const isBlocked = health.status === 'blocked';
  // Tailwind can't resolve dynamically-composed class strings — keep both
  // colour variants as static literals.
  const palette = isBlocked
    ? {
        wrapper: 'border-rose-500/45 shadow-rose-500/20',
        eyebrow: 'text-rose-300',
        icon: 'text-rose-400',
        iconBg: 'bg-rose-500/15',
      }
    : {
        wrapper: 'border-amber-500/45 shadow-amber-500/20',
        eyebrow: 'text-amber-300',
        icon: 'text-amber-400',
        iconBg: 'bg-amber-500/15',
      };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/admin/email/test-send', { to: makerEmail });
      if (data.ok) {
        toast.success(`Test email delivered → ${data.to}`, {
          description: data.note || `Sent via ${data.from_used}`,
          duration: 9000,
        });
      } else {
        toast.error('Test send failed at both primary + fallback.', {
          description: data.fallback_error || data.primary_error,
          duration: 12000,
        });
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed.');
    } finally { setTesting(false); }
  };

  return (
    <div
      data-testid="maker-email-health"
      className={`fixed top-20 left-1/2 -translate-x-1/2 z-[58] w-[min(560px,calc(100vw-32px))]
                  border rounded-xl px-4 py-3 shadow-2xl backdrop-blur-md
                  bg-[#1A0F0A]/95 ${palette.wrapper}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${palette.iconBg}`}>
          <Mail className={`w-4 h-4 ${palette.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.20em] font-bold ${palette.eyebrow}`}>
            Email delivery · {isBlocked ? 'blocked' : 'degraded'}
          </div>
          <div className="text-sm font-semibold text-white mt-0.5">
            {isBlocked
              ? 'Magic-link & recovery emails are NOT being delivered.'
              : 'Email delivery is partially failing.'}
          </div>
          {health.transport?.primary && (
            <div className="mt-1 text-[10px] text-slate-400">
              Transport:&nbsp;
              <span className="font-mono text-slate-200">
                {health.transport.primary === 'smtp'
                  ? `${health.transport.smtp_host}:${health.transport.smtp_port}`
                  : health.transport.primary}
              </span>
            </div>
          )}
          {health.action_hint && (
            <p className="mt-1.5 text-[11px] text-slate-300 leading-relaxed">
              {health.action_hint}
            </p>
          )}
          {health.last_error && (
            <details className="mt-2 text-[10px] text-slate-400">
              <summary className="cursor-pointer hover:text-white">
                Verbatim transport error
              </summary>
              <pre className="mt-1 p-2 rounded bg-black/40 border border-white/5 text-[10px] text-amber-200 whitespace-pre-wrap break-words max-h-32 overflow-auto">
                {health.last_error}
              </pre>
            </details>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              onClick={sendTest}
              disabled={testing}
              data-testid="maker-email-test-send"
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] bg-[#8B3FC5] text-white rounded hover:bg-[#AE52EA] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3 h-3 ${testing ? 'animate-spin' : ''}`} />
              {testing ? 'Sending…' : `Send test → ${makerEmail}`}
            </button>
            <a
              href="https://hpanel.hostinger.com"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="maker-email-hostinger-link"
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300 border border-amber-500/40 rounded hover:bg-amber-500/10 inline-flex items-center gap-1.5"
            >
              Open Hostinger
              <ExternalLink className="w-3 h-3" />
            </a>
            <span className="text-[10px] text-slate-500">
              {health.recent_failures} fail · {health.recent_successes} ok (last 5)
            </span>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          data-testid="maker-email-health-dismiss"
          className="text-slate-500 hover:text-white shrink-0"
          title="Dismiss for this session"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
